import type { ReactNode } from 'react'

import { serviceAssets } from '../../../constants/assets'
import {
  ALL_SERVICES,
  serviceTabCategories,
  serviceTabLabels,
  type ServiceCategoryFilter,
} from '../../../data/services'
import { cn } from '../../../utils/cn'

type TabConfig = {
  id: ServiceCategoryFilter
  label: string
  width: number
  icon: ReactNode
}


const HotelFilterIcon = () => (
  <span
    aria-hidden
    className="relative block h-[18px] w-[30px] shrink-0"
  >
    <img
      alt=""
      src={serviceAssets.filterHotelA}
      className="
        absolute
        left-[14px]
        top-0
        block
        h-[18px]
        w-[18px]
        max-w-none
        object-contain
      "
    />

    <img
      alt=""
      src={serviceAssets.filterHotelB}
      className="
        absolute
        left-0
        top-[7px]
        block
        h-[15px]
        w-[23px]
        max-w-none
        object-contain
      "
    />
  </span>
)


const MirroredFilterIcon = ({
  src,
  className,
}: {
  src: string
  className: string
}) => (
  <span
    aria-hidden
    className={cn(
      'relative block shrink-0 overflow-hidden',
      className,
    )}
  >
    <span className="absolute inset-0 flex -scale-x-100 rotate-180 items-center justify-center">
      <img
        alt=""
        src={src}
        className="
          block
          h-full
          w-full
          max-w-none
          object-contain
        "
      />
    </span>
  </span>
)


const tabs: TabConfig[] = [
  {
    id: ALL_SERVICES,
    label: 'All',
    width: 107,

    icon: (
      <img
        alt=""
        aria-hidden
        src={serviceAssets.filterAll}
        className="
          block
          h-[18px]
          w-[18px]
          max-w-none
          shrink-0
          object-contain
        "
      />
    ),
  },

  ...serviceTabCategories.map((category) => {
    const label = serviceTabLabels[category]

    
    const iconByCategory: Record<
      (typeof serviceTabCategories)[number],
      ReactNode
    > = {
      'airport-transfers': (
        <MirroredFilterIcon
          className="h-[18px] w-[22px]"
          src={serviceAssets.filterAirport}
        />
      ),

      'hotel-transfers': <HotelFilterIcon />,

      'ski-transfers': (
        <MirroredFilterIcon
          className="h-[19px] w-[22px]"
          src={serviceAssets.filterSki}
        />
      ),

      'day-trips': (
        <MirroredFilterIcon
          className="h-[21px] w-[22px]"
          src={serviceAssets.filterDayTrips}
        />
      ),

      'private-van-tours': (
        <MirroredFilterIcon
          className="h-[18px] w-[22px]"
          src={serviceAssets.filterPrivateVan}
        />
      ),

      fleets: (
        <MirroredFilterIcon
          className="h-[18px] w-[22px]"
          src={serviceAssets.filterFleets}
        />
      ),
    }

   
    const widthByCategory: Record<
      (typeof serviceTabCategories)[number],
      number
    > = {
      'airport-transfers': 179,
      'hotel-transfers': 179,
      'ski-transfers': 179,
      'day-trips': 179,
      'private-van-tours': 188,
      fleets: 130,
    }

    return {
      id: category,
      label,
      width: widthByCategory[category],
      icon: iconByCategory[category],
    }
  }),
]

type ServiceCategoryTabsProps = {
  value: ServiceCategoryFilter
  onChange: (value: ServiceCategoryFilter) => void
}


const TabButton = ({
  tab,
  active,
  onSelect,
}: {
  tab: TabConfig
  active: boolean
  onSelect: () => void
}) => (
  <button
    type="button"
    aria-pressed={active}
    onClick={onSelect}
    style={{
      width: tab.width,
    }}
    className={cn(
      `
        flex
        h-[42px]
        shrink-0
        items-center
        justify-center
        rounded-[12px]
        transition-colors
        motion-reduce:transition-none
      `,

      active
        ? `
            border-2
            border-solid
            border-dash-lime
            bg-dash-svc-tab-active
          `
        : `
            border
            border-solid
            border-dash-svc-tab-border
            bg-white
            hover:bg-dash-row-head-bg
          `,
    )}
  >
    <span className="flex items-center gap-[10px]">
      {/* Icon */}
      {tab.icon}

      {/* Label */}
      <span
        className="
          whitespace-nowrap
          font-host
          text-[16px]
          font-medium
          leading-normal
          tracking-[0.32px]
          text-black
        "
      >
        {tab.label}
      </span>
    </span>
  </button>
)


export const ServiceCategoryTabs = ({
  value,
  onChange,
}: ServiceCategoryTabsProps) => {
  return (
    <div className="flex flex-wrap items-center justify-center gap-[13px]">
      {tabs.map((tab) => (
        <TabButton
          key={tab.id}
          tab={tab}
          active={value === tab.id}
          onSelect={() => onChange(tab.id)}
        />
      ))}
    </div>
  )
}